<template>
    <div class="py-4 container-fluid">
        <soft-alert
            v-if="alertTable.state"
            :color="alertTable.color"
            class="mb-3"
            dismissible
        >
            <span class="text-sm">{{ alertTable.message }}</span>
        </soft-alert>

        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
            <router-link :to="{ name: 'Lonjas' }">
                <soft-button variant="outline" color="secondary" size="sm">
                    <i class="fas fa-arrow-left me-1"></i>
                    Volver a lonjas
                </soft-button>
            </router-link>
            <div v-if="lonjaDetail" class="d-flex gap-2">
                <soft-button
                    variant="outline"
                    color="primary"
                    size="sm"
                    @click="editLonja"
                >
                    <i class="fas fa-edit me-1"></i>
                    Editar
                </soft-button>
            </div>
        </div>

        <soft-spinner v-if="spinner" />

        <div v-else-if="lonjaDetail">
            <!-- Info principal -->
            <div class="card mb-4">
                <div class="card-body">
                    <div class="d-flex align-items-start gap-3 flex-wrap">
                        <div class="lonja-logo-lg">
                            <img
                                v-if="lonjaDetail.logo"
                                :src="lonjaDetail.logo"
                                :alt="lonjaDetail.name"
                                @error="onLogoError"
                            />
                            <i v-else class="fas fa-building text-secondary"></i>
                        </div>
                        <div class="flex-grow-1">
                            <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
                                <h4 class="mb-0 font-weight-bolder">{{ lonjaDetail.name }}</h4>
                                <soft-badge
                                    :color="lonjaDetail.active ? 'success' : 'secondary'"
                                    variant="gradient"
                                    size="sm"
                                >
                                    {{ lonjaDetail.active ? 'Activa' : 'Inactiva' }}
                                </soft-badge>
                            </div>
                            <p class="text-xs text-muted mb-2">
                                <code>{{ lonjaDetail.slug }}</code>
                            </p>
                            <p v-if="lonjaDetail.description" class="text-sm text-dark mb-0">
                                {{ lonjaDetail.description }}
                            </p>
                            <p v-else class="text-sm text-muted fst-italic mb-0">
                                Sin descripción
                            </p>
                        </div>
                        <div class="text-end">
                            <div class="text-xs text-muted">Inmobiliarias miembros</div>
                            <div class="h3 mb-0 font-weight-bolder text-primary">
                                {{ members.length }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Miembros -->
            <div class="card">
                <div class="card-header pb-0">
                    <div class="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="mb-0 font-weight-bolder">
                                <i class="fas fa-users me-2 text-primary"></i>
                                Inmobiliarias miembros
                            </h6>
                            <p class="text-xs text-secondary mb-0">
                                Usuarios admin que comparten el MLS de esta lonja
                            </p>
                        </div>
                        <soft-button
                            variant="gradient"
                            color="primary"
                            size="sm"
                            @click="showModalAddMember"
                        >
                            <i class="fas fa-plus me-1"></i>
                            Agregar Inmobiliaria
                        </soft-button>
                    </div>
                </div>

                <div class="card-body px-0 pt-3 pb-2">
                    <div class="table-responsive p-0">
                        <table class="table align-items-center mb-0">
                            <thead>
                                <tr>
                                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                        Logo
                                    </th>
                                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                        Nombre
                                    </th>
                                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                        Email
                                    </th>
                                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                        Empresa
                                    </th>
                                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                        Ingreso
                                    </th>
                                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="!members.length">
                                    <td colspan="6" class="text-center py-5">
                                        <i
                                            class="fas fa-users-slash text-secondary d-block mb-2"
                                            style="font-size: 2rem; opacity: 0.4;"
                                        ></i>
                                        <span class="text-secondary text-sm">
                                            Esta lonja aún no tiene inmobiliarias
                                        </span>
                                    </td>
                                </tr>
                                <tr v-for="user in members" :key="user.id">
                                    <td>
                                        <div class="avatar-small">
                                            <img
                                                v-if="userLogo(user)"
                                                :src="userLogo(user)"
                                                alt="Logo"
                                                @error="onLogoError"
                                            />
                                            <i v-else class="fas fa-building text-secondary"></i>
                                        </div>
                                    </td>
                                    <td>
                                        <p class="text-sm font-weight-bold mb-0 text-dark">
                                            {{ userDisplayName(user) }}
                                        </p>
                                    </td>
                                    <td>
                                        <span class="text-xs text-secondary">{{ user.email }}</span>
                                    </td>
                                    <td>
                                        <span class="text-xs text-dark">
                                            {{ user.empresa?.nombre || '—' }}
                                        </span>
                                    </td>
                                    <td>
                                        <span class="text-xs text-muted">
                                            {{ formatDate(user.pivot?.joined_at || user.joined_at) }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <a
                                            href="#"
                                            class="table-action-link text-danger"
                                            @click.prevent="promptRemoveMember(user)"
                                            title="Remover"
                                        >
                                            <i class="fas fa-user-times"></i>
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="text-center py-5">
            <i class="fas fa-exclamation-circle text-secondary mb-2 d-block" style="font-size: 2rem;"></i>
            <span class="text-secondary">No se encontró la lonja</span>
        </div>

        <!-- Modal editar -->
        <soft-modal
            @close="toggleModalForm"
            :modalActive="modalActiveForm"
            v-if="modalActiveForm"
            size="lg"
        >
            <template #header>
                <h5 class="modal-title mb-0">
                    <i class="fas fa-building me-2 text-primary"></i>
                    Editar Lonja
                </h5>
            </template>
            <form-lonja :lonja="selectedLonja" @close="onFormClose" />
        </soft-modal>

        <!-- Modal agregar inmobiliaria -->
        <soft-modal
            @close="toggleModalAddMember"
            :modalActive="modalActiveAddMember"
            v-if="modalActiveAddMember && lonjaDetail"
            size="lg"
        >
            <template #header>
                <h5 class="modal-title mb-0">
                    <i class="fas fa-user-plus me-2 text-primary"></i>
                    Agregar Inmobiliaria a {{ lonjaDetail.name }}
                </h5>
            </template>
            <add-member-modal
                :lonjaId="lonjaDetail.id"
                :existingMemberIds="existingMemberIds"
                @close="toggleModalAddMember"
                @added="onMemberAdded"
            />
        </soft-modal>

        <!-- Modal confirmar remover miembro -->
        <soft-modal
            @close="cancelRemoveMember"
            :modalActive="!!memberToRemove"
            v-if="memberToRemove"
            size="sm"
        >
            <div class="text-center p-3">
                <div class="lonja-delete-icon mx-auto mb-3">
                    <i class="fas fa-user-times text-danger" style="font-size: 1.75rem;"></i>
                </div>
                <h5 class="font-weight-bolder mb-2">Remover Inmobiliaria</h5>
                <p class="text-sm text-secondary mb-3">
                    ¿Remover a <strong>{{ userDisplayName(memberToRemove) }}</strong> de esta lonja?
                    Dejará de ver el MLS compartido.
                </p>
                <div class="d-flex gap-2 justify-content-center">
                    <soft-button
                        variant="outline"
                        color="secondary"
                        size="sm"
                        @click="cancelRemoveMember"
                    >
                        Cancelar
                    </soft-button>
                    <soft-button
                        variant="gradient"
                        color="danger"
                        size="sm"
                        @click="confirmRemoveMember"
                        :loading="isLoading"
                    >
                        <i class="fas fa-user-times me-1"></i>
                        Remover
                    </soft-button>
                </div>
            </div>
        </soft-modal>

        <div v-if="isLoading" class="loading-overlay">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import SoftButton from '@/components/SoftButton.vue';
import SoftBadge from '@/components/SoftBadge.vue';
import SoftModal from '@/components/SoftModal.vue';
import SoftAlert from '@/components/SoftAlert.vue';
import SoftSpinner from '@/components/SoftSpinner.vue';
import FormLonja from './components/formLonja.vue';
import AddMemberModal from './components/addMemberModal.vue';
import { useLonja } from './useLonja.js';

export default {
    name: 'LonjaDetail',
    components: {
        SoftButton,
        SoftBadge,
        SoftModal,
        SoftAlert,
        SoftSpinner,
        FormLonja,
        AddMemberModal
    },
    setup() {
        const route = useRoute();
        const {
            spinner,
            isLoading,
            alertTable,
            lonjaDetail,
            modalActiveForm,
            modalActiveAddMember,
            selectedLonja,
            getLonjaDetail,
            showModalForm,
            toggleModalForm,
            showModalAddMember,
            toggleModalAddMember,
            removeMember,
        } = useLonja();

        const memberToRemove = ref(null);

        const loadDetail = () => {
            const id = route.params.id;
            if (id) getLonjaDetail(id);
        };

        loadDetail();
        watch(() => route.params.id, loadDetail);

        const members = computed(() => lonjaDetail.value?.inmobiliarias || []);
        const existingMemberIds = computed(() => members.value.map((m) => m.id));

        const userDisplayName = (user) => {
            if (!user) return '';
            if (user.userdata?.primer_nombre) {
                const nombre = user.userdata.primer_nombre;
                const apellido = user.userdata.primer_apellido || '';
                return `${nombre} ${apellido}`.trim();
            }
            return user.empresa?.nombre || user.name || user.email || 'Sin nombre';
        };

        const userLogo = (user) => {
            return user?.empresa?.logo || user?.userdata?.foto || null;
        };

        const formatDate = (dateStr) => {
            if (!dateStr) return '—';
            try {
                const d = new Date(dateStr);
                return d.toLocaleDateString('es-CO', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            } catch {
                return dateStr;
            }
        };

        const editLonja = () => {
            showModalForm(lonjaDetail.value);
        };

        const onFormClose = async (message) => {
            toggleModalForm();
            if (message) {
                await getLonjaDetail(route.params.id);
            }
        };

        const onMemberAdded = () => {
            // addMember ya actualizó el store; refrescar para traer datos completos del pivot
            getLonjaDetail(route.params.id);
        };

        const promptRemoveMember = (user) => {
            memberToRemove.value = user;
        };

        const cancelRemoveMember = () => {
            memberToRemove.value = null;
        };

        const confirmRemoveMember = async () => {
            if (!memberToRemove.value || !lonjaDetail.value) return;
            const userId = memberToRemove.value.id;
            const lonjaId = lonjaDetail.value.id;
            memberToRemove.value = null;
            const result = await removeMember(lonjaId, userId);
            if (result.success) {
                await getLonjaDetail(route.params.id);
            }
        };

        const onLogoError = (e) => {
            e.target.style.display = 'none';
        };

        return {
            spinner,
            isLoading,
            alertTable,
            lonjaDetail,
            members,
            existingMemberIds,
            modalActiveForm,
            modalActiveAddMember,
            selectedLonja,
            memberToRemove,
            userDisplayName,
            userLogo,
            formatDate,
            editLonja,
            onFormClose,
            toggleModalForm,
            showModalAddMember,
            toggleModalAddMember,
            onMemberAdded,
            promptRemoveMember,
            cancelRemoveMember,
            confirmRemoveMember,
            onLogoError
        };
    }
};
</script>

<style lang="scss" scoped>
.lonja-logo-lg {
    width: 80px;
    height: 80px;
    border-radius: 0.75rem;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-size: 2rem;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.avatar-small {
    width: 36px;
    height: 36px;
    border-radius: 0.5rem;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.table-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 0.375rem;
    color: #8392AB;
    transition: all 0.15s ease;
    text-decoration: none;

    &:hover {
        background-color: #f0f2f5;
        color: #344767;
    }

    &.text-danger:hover {
        background-color: #fef2f2;
        color: #ea0606;
    }
}

.modal-title {
    color: #344767;
    font-weight: 600;
}

.lonja-delete-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #fef2f2;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    backdrop-filter: blur(1px);
}
</style>
